import React, { useContext, useEffect, useState } from 'react';
import {
    Form, Input, Button, Card, Typography, Space, Divider, PageHeader,
} from 'antd';
import { FaEdit } from 'react-icons/fa';
import { MdCancel } from 'react-icons/md';
import { ToursContext } from '../../context/tours.context';

const { TextArea } = Input;
const { Text } = Typography;

const StepForm: React.FC = () => {
    const {
        selectedElement,
        stepSelected,
        addStep,
        editStep,
        cancelAddingStep,
        changeElementSelection,
        selectElement,
        setStepSelected,
        isAddingStep,
    } = useContext(ToursContext);

    const [form] = Form.useForm();
    const [canSave, setCanSave] = useState(false);

    const handleSubmit = (values: any) => {
        if (!selectedElement || !canSave) return;

        if (stepSelected) {
            const updatedStep = {
                ...stepSelected,
                selector: selectedElement.selector,
                title: values.title,
                description: values.description || '',
            };
            editStep(
                stepSelected.id,
                updatedStep,
            );
            form.resetFields();
            cancelAddingStep();
            setStepSelected(null);
            return;
        }

        const stepData = {
            selector: selectedElement.selector,
            title: values.title,
            description: values.description || '',
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };

        addStep(stepData);
        form.resetFields();
    };

    const handleCancel = () => {
        form.resetFields();
        setStepSelected(null);
        cancelAddingStep();
    };

    const handleChangeSelection = () => {
        changeElementSelection();
    };

    const handleFormChange = () => {
        const values = form.getFieldsValue();
        const hasSelector = !!selectedElement;
        const hasTitle = !!values.title?.trim();
        const hasDescription = !!values.description?.trim();

        setCanSave(hasSelector && hasTitle && hasDescription);
    };

    useEffect(() => {
        if (selectedElement) {
            form.setFieldsValue({
                selector: selectedElement.selector,
            });
            handleFormChange();
        }
    }, [selectedElement, form]);

    useEffect(() => {
        if (stepSelected) {
            form.setFieldsValue({
                ...stepSelected,
            });
            selectElement(stepSelected.selector);
            handleFormChange();
        } else {
            form.resetFields();
        }
    }, [stepSelected, form]);

    if (!isAddingStep && !stepSelected) {
        return null;
    }

    return (
        <Card bordered style={{ marginBottom: 16 }}>
            <PageHeader
                title="Create New Step"
                style={{ padding: 0 }}
                extra={[

                    <Button
                        type="default"
                        danger
                        icon={<MdCancel size={12} />}
                        onClick={handleCancel}
                        size="small"
                        className="flex-row center-row-center"
                    />,
                ]}
            />
            <div style={{ marginTop: 16 }}>
                {!selectedElement ? (
                    <Space
                        direction="vertical"
                        align="center"
                        style={{
                            width: '100%',
                            textAlign: 'center',
                            padding: '20px 0',
                            borderRadius: 4,
                            border: '1px dashed #52c41a',
                        }}
                    >
                        <Text strong style={{ color: '#52c41a' }}>
                            🎯 Selection Mode Active
                        </Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Click on any dashboard element
                            <br />
                            to capture its CSS selector
                        </Text>
                    </Space>
                ) : (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        onFieldsChange={handleFormChange}
                        size="small"
                    >
                        <Space
                            direction="vertical"
                            style={{
                                width: '100%',
                                marginBottom: 16,
                                padding: 8,
                                borderRadius: 4,
                                fontSize: '12px',
                            }}
                        >
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Text strong>✅ Element selected:</Text>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<FaEdit size={10} />}
                                    onClick={handleChangeSelection}
                                    style={{ padding: '0 4px', height: 'auto', fontSize: '10px' }}
                                >
                                    Change
                                </Button>
                            </Space>
                            <Text code style={{ fontSize: '11px' }}>
                                {selectedElement.selector}
                            </Text>
                        </Space>

                        <Form.Item
                            label="Step Title"
                            name="title"
                            rules={[
                                { required: true, message: 'Title is required' },
                            ]}
                        >
                            <Input placeholder="e.g. Click the new order button" />
                        </Form.Item>

                        <Form.Item
                            label="Step Description"
                            name="description"
                            rules={[
                                { required: true, message: 'Description is required' },
                            ]}
                        >
                            <TextArea
                                rows={3}
                                placeholder="Explain what the user should do in this step..."
                            />
                        </Form.Item>

                        <Divider style={{ margin: '12px 0' }} />

                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={handleCancel} size="small">
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="small"
                                disabled={!canSave}
                            >
                                Save Step
                            </Button>
                        </Space>

                        <Space
                            style={{
                                marginTop: 8,
                                fontSize: '11px',
                                color: canSave ? '#52c41a' : '#faad14',
                            }}
                        >
                            <Text style={{ fontSize: '11px', color: 'inherit' }}>
                                {canSave ? '✅ Ready to save' : '⏳ Complete title and description'}
                            </Text>
                        </Space>
                    </Form>
                )}
            </div>
        </Card>
    );
};

export default StepForm;
